import type { ApiResponse, FieldError } from '@vagusx/shared';

/**
 * HTTP error thrown by API clients.
 * Contains structured error information from the API response.
 */
export class HttpError extends Error {
  public readonly type: string;
  public readonly details?: Record<string, unknown>;

  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fieldErrors: FieldError[] = [],
    public readonly requestId: string = '',
    options?: { type?: string; details?: Record<string, unknown> }
  ) {
    super(message);
    this.name = 'HttpError';
    this.type = options?.type ?? 'system';
    this.details = options?.details;
  }

  /**
   * Create HttpError from API response.
   */
  static fromResponse(response: ApiResponse<never>): HttpError {
    const err = response.error!;
    return new HttpError(err.status, err.code, err.message, err.errors ?? [], response.requestId, {
      type: err.type ?? 'system',
      details: err.details,
    });
  }

  /**
   * Check if this is a validation error (422).
   */
  get isValidation(): boolean {
    return this.status === 422;
  }

  /**
   * Check if this is an unauthorized error (401).
   */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * Check if this is a forbidden error (403).
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Check if this is a not found error (404).
   */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * Check if this is a conflict error (409).
   */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /**
   * Check if this is a duplicate record error.
   */
  get isDuplicate(): boolean {
    return this.code === 'DUPLICATE_RECORD' || this.code === 'ALREADY_EXISTS';
  }

  /**
   * Check if this is a rate limit error (429).
   */
  get isRateLimited(): boolean {
    return this.status === 429 || this.code === 'RATE_LIMIT_EXCEEDED';
  }

  /**
   * Check if this is a server error (5xx).
   */
  get isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * Get the retry-after value in seconds, if available.
   */
  get retryAfter(): number | undefined {
    return this.details?.retryAfter as number | undefined;
  }

  /**
   * Check if the error is for a specific field.
   */
  hasFieldError(field: string): boolean {
    return this.fieldErrors.some((e) => e.field === field);
  }

  /**
   * Get error message for a specific field.
   */
  getFieldError(field: string): string | undefined {
    return this.fieldErrors.find((e) => e.field === field)?.message;
  }
}
